import type { KeyboardEvent, MouseEvent } from 'react';

import { FormFieldChoiceLabel } from '@components/core-ui/form-elements/FormFieldChoiceLabel';
import { FormFieldDisabledGroup } from '@components/core-ui/form-elements/FormFieldDisabledGroup';
import { getRadioMarkClassName } from './helpers/getRadioMarkClassName';
import { TProps } from './types/TProps';

const radioWrapperClassName = 'group relative block h-5 select-none pl-[25px]';

const radioControlClassName = 'relative flex h-5 w-[25px] shrink-0 items-center';

export const Radio = ({
  checked = false,
  onChange,
  disabled = false,
  name,
  className = 'radio',
  testId,
  label,
  children,
}: TProps) => {
  const labelContent = label ?? children;
  const hasLabel = labelContent !== undefined && labelContent !== null && labelContent !== false;

  const activate = () => {
    if (disabled) {
      return;
    }
    onChange(true);
  };

  const handlePointerDown = (event: MouseEvent<HTMLLabelElement>) => {
    event.stopPropagation();
  };

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    activate();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLabelElement>) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };

  const markClassName = getRadioMarkClassName({
    checked,
    disabled,
    hasLabel,
  });

  return (
    <FormFieldDisabledGroup disabled={disabled}>
      <label
        className={[
          'group relative select-none',
          disabled ? '' : 'cursor-pointer [&_svg]:cursor-pointer',
          hasLabel ? 'flex items-center gap-2' : radioWrapperClassName,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={handlePointerDown}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="radio"
        aria-checked={checked}
        data-testid={testId}
      >
        {hasLabel ? (
          <>
            <span className={radioControlClassName}>
              <span className={markClassName} />
            </span>
            <FormFieldChoiceLabel disabled={disabled}>{labelContent}</FormFieldChoiceLabel>
          </>
        ) : (
          <span className={markClassName} />
        )}
      </label>
      {name ? (
        <input type="radio" name={name} checked={checked} readOnly className="hidden" tabIndex={-1} aria-hidden />
      ) : null}
    </FormFieldDisabledGroup>
  );
};
